import {AfterViewInit, Component, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  fadeInLeftOnEnterAnimation,
  fadeInOnEnterAnimation,
  fadeOutOnLeaveAnimation,
  fadeOutRightOnLeaveAnimation
} from 'angular-animations';
import {AnalyticsService} from '../../../services/analytics.service';
import {Subscription} from 'rxjs';
import {UserService} from '../../../services/user.service';
import {ActivatedRoute} from '@angular/router';
import {NavService} from '../../../services/nav.service';
import {JourneyService} from '../../journey/service/journey.service';
import {TasksModel} from '../../tasks/models/tasks.model';
import {NapiService} from '../../../services/napi.service';
import {ConferenceService} from '../service/conference.service';
import {IonSlides} from '@ionic/angular';

interface ChangeIsResultsDto {
  isResults: boolean;
}

@Component({
  selector: 'app-conference-results-page',
  templateUrl: './results.page.html',
  styleUrls: ['./results.page.scss'],
  animations: [
    fadeInOnEnterAnimation({anchor: 'enter', duration: 500}),
    fadeOutOnLeaveAnimation({anchor: 'leave', duration: 500}),
    fadeOutRightOnLeaveAnimation({anchor: 'leaveList', duration: 300}),
    fadeInLeftOnEnterAnimation({anchor: 'enterList', duration: 300}),
  ]
})

export class ConferenceResultsPageComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];

  task: TasksModel = null;

  interactionId = null;
  interaction = null;
  cards = [];
  cardTypes: string[] = ['quiz', 'form', 'poll', 'rating', 'dragAndDrop', 'dragAndMatch', 'dragToGroup'];

  isInteractionActive = false;
  isResultsActive = false;
  isWordCloudReady = false;

  autoplayTimer = null;

  slideOpts = {
    shortSwipes: false,
    longSwipes: true,
    speed: 500
  };

  socket = null;

  @ViewChild(IonSlides, {static: true, read: IonSlides}) slides: IonSlides;

  constructor(
    private ngZone: NgZone,
    private analytics: AnalyticsService,
    private activatedRoute: ActivatedRoute,
    private nav: NavService,
    public userService: UserService,
    private napiService: NapiService,
    private journeyService: JourneyService,
    private conferenceService: ConferenceService,
  ) {

    const task_id = parseInt(this.activatedRoute.snapshot.paramMap.get('task_id'), 10) || 0;
    if (!task_id) {
      this.nav.navigateHome();
    } else {
      this.journeyService.getMyTask(task_id).then(task => {
        this.task = task;
        if (!task) {
          this.nav.navigateHome();
        } else {
          // If interaction ID is provided, then initialize WebSocket
          if (this.task.field2) {
            this.initInteraction(this.task.field2);
            this.initWebSocket(this.task.field2);
          }
        }
      });
    }
  }

  ngOnInit() {
  }

  ionViewDidEnter() {
    this.analytics.trackView('Conference Results');
  }

  protected initInteraction(interactionId: string) {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
    }

    this.conferenceService.getActiveCards(interactionId).then(interaction => {
      if (interaction) {
        this.interactionId = interactionId;
        this.interaction = interaction;

        if (interaction.interactionsGroups
          && interaction.interactionsGroups.length > 0
          && interaction.interactionsGroups[0].interactionsCards
          && interaction.interactionsGroups[0].interactionsCards.length > 0
        ) {
          this.isInteractionActive = true;
          this.isResultsActive = interaction.interactionsGroups[0].isResults;
          this.isWordCloudReady = this.isResultsActive;
          this.cards = interaction.interactionsGroups[0].interactionsCards
            .filter(card => this.cardTypes.includes(card.type));

          if (this.isResultsActive && this.cards.length > 0) {
            if (this.autoplayTimer) {
              clearInterval(this.autoplayTimer);
            }
            this.autoplayTimer = this.startAutoplay();
          }

          console.log('this.isResultsActive', this.isResultsActive);
        } else {
          this.isInteractionActive = false;
          this.isResultsActive = false;
          this.isWordCloudReady = false;
        }
      } else {
        this.clearInteraction();
      }
    }).catch(() => {
      this.clearInteraction();
    });
  }

  protected startAutoplay() {
    return setInterval(() => {
      this.slides.isEnd().then(isEnd => {
        if (isEnd) {
          this.slides.slideTo(0);
        } else {
          this.slides.slideNext();
        }
      });
    }, 5000);
  }

  protected clearInteraction() {
    this.interactionId = null;
    this.interaction = null;
    this.isInteractionActive = false;

    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
    }
  }

  protected initWebSocket(interactionId: string) {
    this.socket = this.napiService.socket('interactions');

    this.socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      setTimeout(() => {
        this.socket.emit('joinRoom', interactionId); // Join to room 'Interaction ID'
      });
    });

    this.socket.on('disconnect', function() {
      console.log('Disconnect client event...');
    });

    this.socket.on('message', data => {
      this.handleInteractionMessage(data);
    });
  }

  protected handleInteractionMessage(payload) {
    console.log('Received message:', payload); // 'setActive', 'setInactive'
    this.ngZone.run(() => {
      switch (payload.message) {
        case 'setActive':
          this.slides.stopAutoplay();
          this.initInteraction(payload.interactionId);
          break;
        case 'setInactive':
          this.slides.stopAutoplay();
          this.initInteraction(payload.interactionId);
          break;
        case 'WordCloudStart':
          this.isWordCloudReady = false;
          break;
        case 'WordCloudComplete':
          this.isWordCloudReady = true;
          break;
        case 'changeIsResults':
          this.slides.stopAutoplay();
          this.changeIsResult(payload.data);
          break;
      }
    });
  }

  protected changeIsResult(payload: ChangeIsResultsDto) {
    this.isWordCloudReady = false;
    this.isResultsActive = payload.isResults;

    if (this.isResultsActive && this.cards.length > 0) {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
      }
      this.autoplayTimer = this.startAutoplay();
    }
  }

  ngOnDestroy(): void {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
    }

    if (this.socket) {
      this.socket.disconnect();
    }

    this.subscriptions.forEach(i => i.unsubscribe());
  }
}
